import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

/** The asset types that carry content the agent supplies, and how to tell whether it did. */
const CONTENT_ASSET_TYPES = [
    VideoData.TEXT_ASSET_TYPE,
    VideoData.MEDIA_ASSET_TYPE,
    VideoData.ICON_ASSET_TYPE,
    VideoData.FANCY_CAPTION_ASSET_TYPE,
    VideoData.CHART_ASSET_TYPE,
] as const;

/**
 * A motion-graphics (codeo) scene: `scene.clear()`ed down to a single HtmlAsset that the design
 * tool authors separately. An empty `html` is its durable "awaiting a prompt" marker, not a scene
 * someone emptied, and the graphic is written by a tool the content checks know nothing about — so
 * these scenes are outside what they judge and are skipped whatever state they are in.
 */
export const isMotionGraphicsScene = (scene: Scene): boolean =>
    (scene.getAssetsByType(VideoData.HTML_ASSET_TYPE) ?? []).length > 0;

export interface ContentAsset {
    name?: string;
    classType?: string;
    isEditable?: boolean;
    text?: string;
    sourceData?: { uuid?: string; url?: string };
    /** When the asset lands, relative to its container — group stagger included. */
    appliedIntroAnimationOffset?: number;
    /** Set on every asset on screen where a match cut joins two scenes. */
    isMatchCutFrozen?: boolean;
    /** The scene or subscene the asset sits on. */
    luminaryParent?: unknown;
}

/**
 * Whether an editable asset is actually showing something. Text needs words; media and icons need
 * a source to render. Any other content asset (a fancy caption, a chart) draws itself, so its
 * presence alone is content.
 */
export const hasContent = (asset: ContentAsset): boolean => {
    switch (asset.classType) {
        case VideoData.TEXT_ASSET_TYPE:
            return !!asset.text?.trim();
        case VideoData.MEDIA_ASSET_TYPE:
            return !!asset.sourceData?.uuid;
        case VideoData.ICON_ASSET_TYPE:
            return !!asset.sourceData?.url;
        default:
            return true;
    }
};

export const editableContentAssets = (scene: Scene): ContentAsset[] =>
    CONTENT_ASSET_TYPES.flatMap(
        (type) => (scene.getAssetsByType(type) ?? []) as unknown as ContentAsset[],
    ).filter((asset) => asset.isEditable !== false);
